// Review: the screen the Author spends the most time in.
//
// The rail on the left says where they are in the Book, the header says what
// Refinement did to this Chapter, the alignment in the middle is the work, and
// the action bar closes it out. Everything expensive is pushed down: the list
// is virtualized and memoized, the diffs are cached per Block, and the two
// text fields (the Instruction and the Block editor) hold their own value, so
// typing anywhere never re-renders a few hundred rows of book text.

import type {
  BlockId,
  BookDetail,
  ChapterId,
  ChapterReview,
  ChapterSummary,
  ScriptVersionId,
  ScriptVersionSummary,
  WordChangeApplyResult,
  WordChangeSuggestions,
} from "@contract";
import { applyChange, detectWordChange } from "@contract";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router";
import { ApiRequestError, errorCodeOf } from "../../../api/fetcher";
import { AudioProvider } from "../../../components/audio/AudioProvider";
import { Button } from "../../../components/control/Button";
import { Banner } from "../../../components/feedback/Banner";
import { EmptyState } from "../../../components/feedback/EmptyState";
import { ErrorBanner } from "../../../components/feedback/ErrorBanner";
import { ErrorState } from "../../../components/feedback/ErrorState";
import { Skeleton } from "../../../components/feedback/Skeleton";
import { ConfirmDialog } from "../../../components/overlay/ConfirmDialog";
import { ShortcutsOverlay } from "../../../components/overlay/ShortcutsOverlay";
import { DraftActions } from "../../../drafts/DraftActions";
import { useWorkspaceDraft } from "../../../drafts/useWorkspaceDraft";
import { useBookDetail } from "../bookContext";
import { ActionBar, INSTRUCTION_FIELD_ID } from "./ActionBar";
import { AlignmentList } from "./AlignmentList";
import { ChapterHeader } from "./ChapterHeader";

import { QualityPanel } from "./QualityPanel";
import { ReviewBanners } from "./ReviewBanners";
import { clampFocus, pairOf, reviewRows } from "./rows";
import { useShortcutGroups } from "./shortcutHelp";
import { isTypingTarget, shortcutFor } from "./shortcuts";
import { useBlockSample } from "./useBlockSample";
import {
  useApproveChapter,
  useChapterReview,
  useEditBlock,
  useRerunChapter,
  useRestoreVersion,
  useRetryJob,
  useStopChapterRender,
} from "./useReview";
import { useApplyWordChange, useSuggestWordChange } from "./useWordChange";
import { VersionsDialog } from "./VersionsDialog";
import { readViewMode, showsManuscript, type ViewMode, writeViewMode } from "./viewMode";
import { WordChangeDialog, type WordChangeSelection } from "./WordChangeDialog";

const localStorageOrNothing = (): Storage | undefined =>
  typeof window === "undefined" ? undefined : window.localStorage;

/** The Chapter Review opens on when the route names none: the first one waiting for the Author. */
function firstReviewable(chapters: readonly ChapterSummary[]): ChapterSummary | undefined {
  const included = chapters.filter((chapter) => !chapter.excluded);
  return (
    included.find((chapter) => chapter.state === "inReview") ??
    included.find((chapter) => chapter.versionCount > 0) ??
    included[0]
  );
}

export function ReviewScreen() {
  const { chapterId } = useParams();
  const { t } = useTranslation("review");
  // The Book comes from BookLayout, which owns the one poll for the whole page
  // and keeps the cached copy on screen behind a banner when a refresh fails.
  // Asking for it again here would put a second observer on the same query
  // key, so the tab would ask for the Book twice every tick and one failed
  // poll would raise a second banner beside the frame's own.
  const book = useBookDetail();

  if (!chapterId) {
    const target = firstReviewable(book.chapters);
    if (!target) {
      return (
        <EmptyState title={t("empty.noChapters")} className="m-9">
          {t("empty.body")}
        </EmptyState>
      );
    }
    return <Navigate to={`/books/${book.id}/review/${target.id}`} replace />;
  }
  return (
    <AudioProvider>
      <ReviewChapter chapterId={chapterId} book={book} />
    </AudioProvider>
  );
}

function ReviewChapter({ chapterId, book }: { chapterId: string; book: BookDetail }) {
  const { t } = useTranslation("review");
  const navigate = useNavigate();
  const bookId = book.id;
  const [search, setSearch] = useSearchParams();
  const requestedVersion = search.get("version") as ScriptVersionId | null;
  const chapter = useChapterReview(bookId, chapterId, requestedVersion ?? undefined);

  const [mode, setMode] = useState<ViewMode>(() =>
    typeof window.matchMedia === "function" && window.matchMedia("(max-width: 767px)").matches
      ? "inline"
      : readViewMode(localStorageOrNothing()),
  );
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [focusNonce, setFocusNonce] = useState(0);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [droppedOpen, setDroppedOpen] = useState(false);
  const [stopOpen, setStopOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const shortcuts = useShortcutGroups();

  const edit = useEditBlock(bookId, chapterId);
  const approve = useApproveChapter(bookId, chapterId);
  const rerun = useRerunChapter(bookId, chapterId);
  const restore = useRestoreVersion(bookId, chapterId);
  const stopRender = useStopChapterRender(bookId, chapterId);
  const retryJob = useRetryJob(bookId, chapterId);
  const sample = useBlockSample(bookId, chapterId);
  const suggestWordChange = useSuggestWordChange(bookId);
  const applyWordChange = useApplyWordChange(bookId);
  const [wordChange, setWordChange] = useState<{
    readonly suggestions: WordChangeSuggestions;
    readonly open: boolean;
    /** A fresh dialog, with fresh checkboxes, for every new set of suggestions. */
    readonly seq: number;
    /** This Chapter's Block texts the offsets were computed against. */
    readonly texts: ReadonlyMap<string, string>;
  } | null>(null);
  const wordChangeSeq = useRef(0);
  const [wordChangeResult, setWordChangeResult] = useState<{
    readonly draftAdded: number;
    readonly result: WordChangeApplyResult | null;
  } | null>(null);

  const review = chapter.data;
  const activeScript = review?.script ?? null;
  const workspace = useWorkspaceDraft(
    bookId,
    `chapter-${chapterId}`,
    { edits: {} as Record<string, string> },
    !!activeScript,
  );
  const script = useMemo(
    () =>
      activeScript?.version.isCurrent && workspace.dirty
        ? {
            ...activeScript,
            pairs: activeScript.pairs.map((pair) =>
              workspace.value.edits[pair.block.id] === undefined
                ? pair
                : {
                    ...pair,
                    block: {
                      ...pair.block,
                      text: workspace.value.edits[pair.block.id] ?? pair.block.text,
                    },
                  },
            ),
          }
        : activeScript,
    [activeScript, workspace.dirty, workspace.value],
  );
  const rows = useMemo(() => reviewRows(script), [script]);
  // What the Block said when the editor opened, and the last text the editor
  // reported. The draft follows every keystroke, so when the editor closes the
  // draft already holds the new text (and the editor takes the "unchanged"
  // path), and the word the Author replaced is only known from here. Escape
  // reports the original text before it closes, so it reads as no change.
  const editSession = useRef<{ blockId: string; before: string; after: string } | null>(null);
  const draftEdits = useRef(workspace.value.edits);
  draftEdits.current = workspace.value.edits;
  const scriptRef = useRef(script);
  scriptRef.current = script;
  const currentChapter = useRef(chapterId);
  currentChapter.current = chapterId;
  const focusedPair = pairOf(rows[focusedIndex]);

  // A Chapter is editable only while nothing is rendering it and only on its
  // current version: an older version is a record of what was, and the way
  // back to it is Restore, not an edit that would fork from history. A
  // Chapter being refined keeps its previous script on screen, and the API
  // refuses every write to it, so the controls have to be off too.
  const locked = review?.lock != null;
  const showingCurrent = script?.version.isCurrent === true;
  const editable =
    review !== undefined &&
    !locked &&
    workspace.loaded &&
    !workspace.applying &&
    showingCurrent &&
    !review.excluded &&
    review.state !== "refining";

  // One answer for the button and for the `a` key, so the keyboard cannot do
  // what the button refuses: approving a locked or refining Chapter, approving
  // the version the Author is already looking at the approval of, or racing
  // an edit whose fork would give the approval a version id from before it.
  const canApprove =
    editable &&
    script !== null &&
    !workspace.dirty &&
    !workspace.applying &&
    !book.drafts?.some(
      (d) => d.key === "guidance" || d.key === "casting" || d.key === "metadata",
    ) &&
    !edit.isPending &&
    !approve.isPending &&
    (book.reviewPolicy !== "exceptions" ||
      book.qualityReviews?.some(
        (q) =>
          q.chapterId === chapterId && q.versionId === script?.version.id && q.status === "passed",
      ) === true) &&
    (!review?.isCalibration || book.casting.narrator !== null) &&
    review?.approval?.versionId !== script.version.id;

  // A different Chapter, or a version the Author asked for by name, starts the
  // pass again at the top. An edit must not: the first change to a refinement
  // version forks a new one, and keying this on the live version id would take
  // the Author from the Block they just committed back to the first one.
  const listKey = `${chapterId}:${requestedVersion ?? "current"}`;
  const lastListKey = useRef(listKey);
  const lastRow = Math.max(0, rows.length - 1);
  if (lastListKey.current !== listKey) {
    lastListKey.current = listKey;
    if (focusedIndex !== 0) setFocusedIndex(0);
    if (editingIndex !== null) setEditingIndex(null);
    // An offer belongs to the Chapter it was made in: its places here are
    // offsets into that Chapter's draft, meaningless in any other.
    editSession.current = null;
    if (wordChange !== null) setWordChange(null);
    if (wordChangeResult !== null) setWordChangeResult(null);
  } else if (focusedIndex > lastRow) {
    // A restore can hand back a shorter script than the one on screen, so the
    // focus is pulled back to the last row rather than left past the end. Only
    // when the list is the same one: on a Chapter change the reset above wins,
    // and running both would queue two updates and land on the last row.
    setFocusedIndex(lastRow);
  }

  const clearVersionParam = useCallback(() => {
    const next = new URLSearchParams(search);
    next.delete("version");
    setSearch(next, { replace: true });
  }, [search, setSearch]);

  const chooseMode = useCallback((next: ViewMode) => {
    setMode(next);
    writeViewMode(localStorageOrNothing(), next);
  }, []);

  const moveFocus = useCallback(
    (delta: number) => {
      setFocusedIndex((current) => clampFocus(current, delta, rows.length));
      setFocusNonce((n) => n + 1);
    },
    [rows.length],
  );

  const startEdit = useCallback(
    (index: number) => {
      if (!editable || rows[index]?.kind !== "pair") return;
      setFocusedIndex(index);
      setEditingIndex(index);
      const pair = pairOf(rows[index]);
      editSession.current = pair
        ? { blockId: pair.block.id, before: pair.block.text, after: pair.block.text }
        : null;
    },
    [editable, rows],
  );

  // Escape and the commit chord put the focus back on the row, because the
  // Author is still on the keyboard and has nowhere else to be. An ordinary
  // blur must not: it happens because they clicked something else, and pulling
  // the focus back would send the next keystroke to the screen's shortcuts
  // instead of into the Instruction field they just aimed at.
  // A finished edit that replaced one word or short name asks where else the
  // Book uses it. The answer is an offer in a strip, never a dialog in the
  // Author's face: most edits are not names, and the next one should not wait.
  const suggestMutate = suggestWordChange.mutate;
  const finishEdit = useCallback(() => {
    const session = editSession.current;
    editSession.current = null;
    if (!session) return;
    const change = detectWordChange(session.before.trim(), session.after.trim());
    if (!change) return;
    setWordChangeResult(null);
    const edits = { ...draftEdits.current, [session.blockId]: session.after };
    const texts = new Map(
      (scriptRef.current?.pairs ?? []).map((pair) => [
        pair.block.id as string,
        edits[pair.block.id] ?? pair.block.text,
      ]),
    );
    const askedIn = chapterId;
    suggestMutate(
      { change, chapterId: chapterId as ChapterId, draftEdits: edits },
      {
        onSuccess: (suggestions) => {
          // The Author may have moved on to another Chapter while this was asked.
          if (currentChapter.current !== askedIn) return;
          setWordChange(
            suggestions.chapters.length || suggestions.styleSheet
              ? { suggestions, open: false, seq: ++wordChangeSeq.current, texts }
              : null,
          );
        },
      },
    );
  }, [chapterId, suggestMutate]);

  const cancelEdit = useCallback(
    (viaKeyboard: boolean) => {
      setEditingIndex(null);
      if (viaKeyboard) setFocusNonce((n) => n + 1);
      finishEdit();
    },
    [finishEdit],
  );

  /** A 409 because the render started under the Author is an offer to stop it, never a raw code. */
  const onConflict = useCallback((error: unknown) => {
    if (error instanceof ApiRequestError && error.code === "CHAPTER_RENDERING") setStopOpen(true);
  }, []);

  // The editor stays open until the server has taken the edit. A refusal —
  // the render started underneath, the Chapter went into Refinement, the
  // connection dropped — would otherwise close it and roll the cache back,
  // and the sentence the Author had just written would be gone with nothing
  // to retry from. `edit.mutate` rather than `edit`: useMutation hands back a
  // fresh result object every render, and depending on it would make this
  // callback new every render and defeat the alignment's memo.
  const setTextDraft = workspace.setValue;
  const changeDraft = useCallback(
    (index: number, text: string) => {
      const pair = pairOf(rows[index]);
      if (!pair) return;
      if (editSession.current?.blockId === pair.block.id) editSession.current.after = text;
      setTextDraft((old) => ({ edits: { ...old.edits, [pair.block.id]: text } }));
    },
    [rows, setTextDraft],
  );
  const commitEdit = useCallback(
    (index: number, text: string, viaKeyboard: boolean) => {
      changeDraft(index, text);
      setEditingIndex(null);
      if (viaKeyboard) setFocusNonce((n) => n + 1);
      finishEdit();
    },
    [changeDraft, finishEdit],
  );

  // Places in this Chapter go into its open draft; everything else goes to the
  // server in one call. The draft text is read now, not when the suggestions
  // came back, and an occurrence the Author has since edited away is skipped.
  const applyWordChangeMutate = applyWordChange.mutate;
  // The server's part goes first and this Chapter's draft changes only once it
  // succeeded, so a failed Apply can be pressed again without changing the
  // draft twice. A Block whose text moved since the search is left alone: its
  // offsets could now point at a different occurrence of the same word.
  const acceptWordChange = useCallback(
    (selection: WordChangeSelection) => {
      if (!wordChange) return;
      const change = wordChange.suggestions.change;
      const applyToDraft = (): number => {
        const current = new Map(
          (scriptRef.current?.pairs ?? []).map((pair) => [
            pair.block.id as string,
            draftEdits.current[pair.block.id] ?? pair.block.text,
          ]),
        );
        let added = 0;
        const next: Record<string, string> = {};
        for (const [blockId, accepted] of selection.draft) {
          const text = current.get(blockId);
          if (text === undefined || text !== wordChange.texts.get(blockId)) continue;
          const result = applyChange(text, change, accepted);
          if (result.applied === 0) continue;
          next[blockId] = result.text;
          added += result.applied;
        }
        if (added) setTextDraft((old) => ({ edits: { ...old.edits, ...next } }));
        return added;
      };
      if (!selection.request) {
        const draftAdded = applyToDraft();
        setWordChange(null);
        setWordChangeResult({ draftAdded, result: null });
        return;
      }
      applyWordChangeMutate(selection.request, {
        onSuccess: (result) => {
          const draftAdded = applyToDraft();
          setWordChange(null);
          setWordChangeResult({ draftAdded, result });
        },
      });
    },
    [applyWordChangeMutate, setTextDraft, wordChange],
  );

  const submitApproval = useCallback(
    (acknowledgeDroppedNodes: boolean) => {
      const versionId = script?.version.id;
      if (!versionId) return;
      setDroppedOpen(false);
      approve.mutate(
        { versionId, acknowledgeDroppedNodes },
        {
          onSuccess: () => navigate(`/books/${bookId}/overview`),
          onError: (error) => {
            if (error instanceof ApiRequestError && error.code === "DROPPED_NODES") {
              setDroppedOpen(true);
              return;
            }
            onConflict(error);
          },
        },
      );
    },
    [approve, onConflict, script, navigate, bookId],
  );

  /**
   * The dialog opens from what the screen already knows rather than from a
   * refusal, so the Author reads what they are about to accept the loss of
   * before they press anything. The 409 is still handled above, because the
   * API is what actually enforces the acknowledgement.
   */
  const requestApproval = useCallback(() => {
    // The same guard the button carries. Without it the `a` key would approve
    // a locked Chapter, a refining one, or an older version by its own id, and
    // two quick presses would post two approvals.
    if (!canApprove || !script) return;
    if (script.stats.droppedNodes > 0) setDroppedOpen(true);
    else submitApproval(false);
  }, [canApprove, script, submitApproval]);

  const playFocused = useCallback(() => {
    if (!book.casting.narrator || !focusedPair) return;
    // The text travels with the id: an edit of a version whose source is
    // already `edit` keeps the Block id and changes the words, and a Sample
    // looked up by id alone would replay the sentence before the edit.
    void sample.play(focusedPair.block.id as BlockId, focusedPair.block.text);
  }, [book.casting.narrator, focusedPair, sample]);

  const viewVersion = useCallback(
    (version: ScriptVersionSummary) => {
      setVersionsOpen(false);
      if (version.isCurrent) {
        clearVersionParam();
        return;
      }
      // Pushed, not replaced: opening an older version out of curiosity is a
      // navigation, and Back has to come back to the current version rather
      // than throw the Author out of Review altogether. Coming back from a
      // restore keeps `replace`, because that is a correction, not a step.
      const next = new URLSearchParams(search);
      next.set("version", version.id);
      setSearch(next);
    },
    [clearVersionParam, search, setSearch],
  );

  const restoreVersion = useCallback(
    (version: ScriptVersionSummary) => {
      setVersionsOpen(false);
      restore.mutate(
        { versionId: version.id },
        { onSuccess: clearVersionParam, onError: onConflict },
      );
    },
    [clearVersionParam, onConflict, restore],
  );

  // Anything modal takes the screen's keyboard with it. A Dialog is a portal
  // on document.body and handles only Escape and Tab, so every other key
  // bubbles out to this listener; without the guard, `p` starts a paid Sample
  // render from behind the shortcuts overlay, `a` opens a second dialog on top
  // of the first, and `e` mounts an autofocusing editor outside the focus trap.
  // Only `?` still runs, because that is how the help closes again.
  const overlayOpen =
    versionsOpen || droppedOpen || stopOpen || shortcutsOpen || wordChange?.open === true;

  // One listener for the whole screen, so a shortcut works wherever the focus
  // sits — except inside something the Author is typing in, where `a` has to
  // stay the letter a.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;
      const action = shortcutFor(event);
      if (!action) return;
      if (overlayOpen && !(shortcutsOpen && action.kind === "help")) return;
      event.preventDefault();
      switch (action.kind) {
        case "focusNext":
          moveFocus(1);
          break;
        case "focusPrevious":
          moveFocus(-1);
          break;
        case "edit":
          startEdit(focusedIndex);
          break;
        case "approve":
          requestApproval();
          break;
        case "instruction":
          document.getElementById(INSTRUCTION_FIELD_ID)?.focus();
          break;
        case "viewMode":
          chooseMode(action.mode);
          break;
        case "playSample":
          playFocused();
          break;
        case "help":
          setShortcutsOpen((open) => !open);
          break;
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    chooseMode,
    focusedIndex,
    moveFocus,
    overlayOpen,
    playFocused,
    requestApproval,
    shortcutsOpen,
    startEdit,
  ]);

  // The Instruction the Author typed is cleared only once the re-run has been
  // accepted. Clearing it on the click would lose the sentence they composed
  // to a 409, a 500, or an offline browser, with nothing left to retry from.
  const rerunMutate = rerun.mutate;
  const rerunChapter = useCallback(
    (instruction: string | null, onAccepted: () => void) => {
      rerunMutate({ instruction }, { onSuccess: onAccepted, onError: onConflict });
    },
    [onConflict, rerunMutate],
  );

  const viewingOlder = script && !script.version.isCurrent ? script.version : null;
  // Every call the Author can make, including the two that used to fail in
  // silence: a Stop that was refused just re-enabled its button while the
  // render carried on, and a Retry that was refused said nothing at all.
  const mutationError =
    workspace.error ??
    edit.error ??
    approve.error ??
    rerun.error ??
    restore.error ??
    stopRender.error ??
    retryJob.error;
  // A Chapter refresh that fails over data that is still on screen is a banner
  // beside the alignment, not a page instead of it. The Book's own staleness is
  // the frame's to report, and it already does.
  const refreshError = chapter.data !== undefined && chapter.isError ? chapter.error : null;

  return (
    // The frame is exactly the viewport under the nav, so the alignment
    // scrolls inside it and the rail stays put. AppShell's `min-h-screen`
    // would otherwise let a long Chapter push the whole page down and take
    // the rail's footer with it.
    <div className="flex min-h-0 w-full flex-1">
      <div className="flex min-w-0 flex-1 flex-col">
        {chapter.isPending ? <Skeleton variant="text" className="m-9" /> : null}
        {chapter.isError && chapter.data === undefined ? (
          <ErrorState
            code={errorCodeOf(chapter.error)}
            onRetry={() => void chapter.refetch()}
            className="m-9"
          />
        ) : null}
        {review ? (
          <>
            <ChapterHeader
              chapter={review}
              mode={mode}
              onModeChange={chooseMode}
              onOpenVersions={() => setVersionsOpen(true)}
            />
            <ReviewBanners
              chapter={review}
              viewing={viewingOlder}
              stopping={stopRender.isPending}
              retrying={retryJob.isPending}
              restoring={restore.isPending}
              onStopRender={() => setStopOpen(true)}
              onRetry={(jobId) => retryJob.mutate(jobId)}
              onBackToCurrent={clearVersionParam}
              onRestoreViewed={restoreVersion}
            />
            {mutationError ? <ErrorBanner error={mutationError} /> : null}
            {refreshError ? <ErrorBanner error={refreshError} /> : null}
            {rows.length === 0 ? null : (
              <ColumnHeadings
                mode={mode}
                editable={editable}
                undoesApproval={review.approval !== null}
              />
            )}
            {rows.length === 0 ? (
              <NoScript chapter={review} />
            ) : (
              <AlignmentList
                rows={rows}
                mode={mode}
                focusedIndex={focusedIndex}
                focusNonce={focusNonce}
                editingIndex={editingIndex}
                editable={editable}
                saving={edit.isPending}
                sampleStatus={
                  book.casting.narrator
                    ? sample.statusFor(focusedPair?.block.id, focusedPair?.block.text)
                    : null
                }
                onFocusRow={setFocusedIndex}
                onEditRow={startEdit}
                onCommit={commitEdit}
                onDraftChange={changeDraft}
                onCancelEdit={cancelEdit}
                onPlay={playFocused}
              />
            )}
            <QualityPanel
              draftPending={workspace.dirty}
              review={review}
              onLocate={(index) => {
                const row = rows.findIndex((r) => pairOf(r)?.block.index === index);
                if (row >= 0) {
                  setFocusedIndex(row);
                  setFocusNonce((n) => n + 1);
                  startEdit(row);
                }
              }}
            />
            <WordChangeStrip
              searching={suggestWordChange.isPending}
              suggestions={wordChange?.suggestions ?? null}
              outcome={wordChangeResult}
              book={book}
              onOpen={() => setWordChange((old) => (old ? { ...old, open: true } : old))}
              onDismiss={() => {
                setWordChange(null);
                setWordChangeResult(null);
              }}
            />
            {wordChange ? (
              <WordChangeDialog
                key={wordChange.seq}
                open={wordChange.open}
                suggestions={wordChange.suggestions}
                book={book}
                busy={applyWordChange.isPending}
                error={applyWordChange.error}
                onApply={acceptWordChange}
                onClose={() => setWordChange((old) => (old ? { ...old, open: false } : old))}
              />
            ) : null}
            {workspace.dirty ? (
              <DraftActions
                draft={workspace}
                label={t("nav:workspace.applyText")}
                disabled={
                  !editable || Object.values(workspace.value.edits).some((text) => !text.trim())
                }
                onApply={() => void workspace.apply().catch(onConflict)}
              >
                <p>{t("nav:workspace.draftText")}</p>
              </DraftActions>
            ) : (
              <ActionBar
                enabled={editable}
                rerunning={rerun.isPending}
                approving={approve.isPending}
                canApprove={canApprove}
                calibration={review.isCalibration && review.approval === null}
                onRerun={rerunChapter}
                onApprove={requestApproval}
              />
            )}
            <VersionsDialog
              open={versionsOpen}
              versions={review.versions}
              viewingId={script?.version.id}
              restorable={!locked && !workspace.dirty}
              busy={restore.isPending}
              onView={viewVersion}
              onRestore={restoreVersion}
              onClose={() => setVersionsOpen(false)}
            />
            <ConfirmDialog
              open={droppedOpen}
              title={t("dropped.dialogTitle")}
              confirmLabel={t("dropped.dialogConfirm")}
              busy={approve.isPending}
              onConfirm={() => submitApproval(true)}
              onCancel={() => setDroppedOpen(false)}
            >
              {t("dropped.dialogBody", { count: script?.stats.droppedNodes ?? 0 })}
            </ConfirmDialog>
            <ConfirmDialog
              open={stopOpen}
              title={t("locked.dialogTitle")}
              confirmLabel={t("locked.stop")}
              tone="danger"
              busy={stopRender.isPending}
              onConfirm={() =>
                stopRender.mutate(undefined, { onSuccess: () => setStopOpen(false) })
              }
              onCancel={() => setStopOpen(false)}
            >
              {t("locked.dialogBody")}
              {/* A refused Stop is read here, where the Author is looking. The
                  banner's copy of it is below the fold once this dialog is up,
                  and the render is still running either way. */}
              {stopRender.error ? (
                <div className="mt-3">
                  <ErrorBanner error={stopRender.error} />
                </div>
              ) : null}
            </ConfirmDialog>
          </>
        ) : null}
        <ShortcutsOverlay
          open={shortcutsOpen}
          onClose={() => setShortcutsOpen(false)}
          groups={shortcuts}
        />
      </div>
    </div>
  );
}

/**
 * The offer after a word change, and afterwards what it did. It sits above the
 * action bar, where the Author looks next, and never takes the keyboard.
 */
function WordChangeStrip({
  searching,
  suggestions,
  outcome,
  book,
  onOpen,
  onDismiss,
}: {
  searching: boolean;
  suggestions: WordChangeSuggestions | null;
  outcome: {
    readonly draftAdded: number;
    readonly result: WordChangeApplyResult | null;
  } | null;
  book: BookDetail;
  onOpen: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation("review");
  if (searching)
    return (
      <Banner tone="working" label={t("wordChange.label")}>
        {t("wordChange.searching")}
      </Banner>
    );
  if (suggestions) {
    const places = suggestions.chapters.reduce((n, c) => n + c.suggestions.length, 0);
    return (
      <Banner
        tone="info"
        label={t("wordChange.label")}
        onDismiss={onDismiss}
        action={
          <Button size="sm" variant="secondary" onClick={onOpen}>
            {t("wordChange.review")}
          </Button>
        }
      >
        {places
          ? t("wordChange.strip", {
              count: places,
              from: suggestions.change.from,
              to: suggestions.change.to,
            })
          : t("wordChange.stripStyleSheet", { term: suggestions.styleSheet?.term ?? "" })}
      </Banner>
    );
  }
  if (!outcome) return null;
  const applied =
    outcome.draftAdded + (outcome.result?.chapters.reduce((n, c) => n + c.applied, 0) ?? 0);
  const skipped = outcome.result?.chapters.filter((c) => c.skipped !== null) ?? [];
  const numberOf = (id: string) => book.chapters.find((c) => c.id === id)?.number ?? "–";
  return (
    <Banner
      tone={skipped.length ? "warn" : "info"}
      label={t("wordChange.label")}
      onDismiss={onDismiss}
    >
      <p className="m-0">
        {t("wordChange.done", { count: applied })}
        {outcome.draftAdded ? ` ${t("wordChange.draftAdded", { count: outcome.draftAdded })}` : ""}
        {outcome.result?.styleSheet?.version ? ` ${t("wordChange.styleSheetDone")}` : ""}
      </p>
      {skipped.map((c) => (
        <p key={c.chapterId} className="m-0 text-data text-ink-2">
          {t("wordChange.skipped", {
            number: numberOf(c.chapterId),
            reason: t(`wordChange.blocked.${c.skipped}`),
          })}
        </p>
      ))}
      {outcome.result?.styleSheet?.skipped ? (
        <p className="m-0 text-data text-ink-2">
          {t(`wordChange.styleSheet.skipped.${outcome.result.styleSheet.skipped}`)}
        </p>
      ) : null}
    </Banner>
  );
}

/**
 * The column labels, once above the list rather than once per row: the
 * mockups put them at the top of each column, and in a virtualized list that
 * has to be a header the rows scroll under.
 */
function ColumnHeadings({
  mode,
  editable,
  undoesApproval,
}: {
  mode: ViewMode;
  editable: boolean;
  /** The Chapter is Approved or Rendered, so an edit costs the approval and stales the audio. */
  undoesApproval: boolean;
}) {
  const { t } = useTranslation("review");
  return (
    <div
      aria-hidden="true"
      className={
        showsManuscript(mode)
          ? "grid shrink-0 grid-cols-2 gap-px border-line border-t bg-line"
          : "grid shrink-0 grid-cols-1 border-line border-t bg-line"
      }
    >
      {showsManuscript(mode) ? (
        <p className="m-0 bg-surface px-9 pt-4 pb-1 font-mono text-label text-ink-3">
          {t("manuscript")}
        </p>
      ) : null}
      <p className="m-0 flex gap-3 bg-surface px-9 pt-4 pb-1 font-mono text-label text-ink-3">
        <span>{t("script")}</span>
        {/* On an Approved or Rendered Chapter the hint says what an edit
            costs, because a commit is one blur away and the only sign
            afterwards is a small "bearbeitet" on the Block. */}
        {editable ? (
          <span className="text-accent">
            {undoesApproval ? t("editHintApproved") : t("editHint")}
          </span>
        ) : null}
      </p>
    </div>
  );
}

/** Why a Chapter has no alignment to show: it is excluded, waiting, refining, or failed. */
function NoScript({ chapter }: { chapter: ChapterReview }) {
  const { t } = useTranslation("review");
  const title = chapter.excluded
    ? t("empty.excluded")
    : chapter.state === "refining"
      ? t("empty.refining")
      : chapter.state === "failed"
        ? t("empty.failed")
        : t("empty.pending");
  return (
    <div className="min-h-0 flex-1 overflow-auto border-line border-y bg-ground p-9">
      {/* A failed Chapter gets its own sentence: the general one promises a
          script will appear here, and after a failure that is not true until
          somebody starts the Refinement again. */}
      <EmptyState title={title}>
        {chapter.state === "failed" ? t("empty.failedBody") : t("empty.body")}
      </EmptyState>
    </div>
  );
}
